import * as React from 'react';
import { type LayoutChangeEvent, View, type ViewProps } from 'react-native';

export interface LayoutDimensions {
    width: number;
    height: number;
}

export type LayoutDimensionsViewProps = ViewProps & {
    children: React.ReactNode;
};

export interface LayoutDimensionsHandle {
    View: React.ComponentType<LayoutDimensionsViewProps>;
    /**
     * Hook to fetch the dimensions of the layout. The second part of the tupple
     * informs us if the dimensions are the final loaded layout of still the hardocded ones.
     */
    useDimensions: () => [dimensions: LayoutDimensions, ready: boolean];
}

/**
 * Create an handle to access the size of a parent from a child view.
 * @argument defaultDimensions Default dimensions to use for an initial render.
 *  Either specify the dimensions for all targets, or optionally give more specific defaults.
 * @argument options.width Should listeners be updated when width is changing (default true)
 * @argument options.height Should listeners be updated when height is changing (default true)
 */
export function createLayoutDimensions(
    defaultDimensions: LayoutDimensions | ((props: LayoutDimensionsViewProps) => LayoutDimensions),
    options: { width?: boolean; height?: boolean } = {}
): LayoutDimensionsHandle {
    const context = React.createContext<{
        ready: boolean;
        dimensions: LayoutDimensions | null;
    } | null>(null);

    /**
     * Parent wrapper that propagate the dimensions in the tree.
     */
    function ViewWithDimensions(props: LayoutDimensionsViewProps): React.ReactElement {
        const { children, ...rest } = props;
        const defaults =
            typeof defaultDimensions === 'object' ? defaultDimensions : defaultDimensions(props);
        const [dimensions, setDimensions] = React.useState<LayoutDimensions | null>(defaults);
        const [ready, setReady] = React.useState<boolean>(false);

        const onLayout = (event: LayoutChangeEvent) => {
            const { layout } = event.nativeEvent;

            const changed =
                !dimensions ||
                (options.width !== false && layout.width !== dimensions.width) ||
                (options.height !== false && layout.height !== dimensions.height);

            if (changed) {
                setDimensions(layout);
                setReady(true);
            }
        };

        return (
            <context.Provider value={{ ready, dimensions }}>
                <View {...rest} onLayout={onLayout}>
                    {children}
                </View>
            </context.Provider>
        );
    }

    /**
     * Hook to access the dimensions and suspend if needed.
     */
    function useDimensions(): [dimensions: LayoutDimensions, ready: boolean] {
        const result = React.useContext(context);

        if (!result || !result.dimensions) {
            throw new Error('Dimensions not available');
        }

        return [result.dimensions, result.ready];
    }

    return {
        View: ViewWithDimensions,
        useDimensions,
    };
}
